import {
  createChartFrame,
  resolveTitle,
  resolvePalette,
  clearSvg,
  getLayout,
  addLabel,
  renderLegend,
  createSvgEl,
  toNumber,
  normalizeSeriesColors
} from './chart-utils.js';

const DEFAULT_AXES = ['Core', 'Flux', 'Drift', 'Pulse', 'Echo'];
const DEFAULT_SERIES = [
  { name: 'Alpha', values: [6, 8, 5, 7, 6] },
  { name: 'Beta', values: [4, 6, 4, 5, 5] }
];

export function render({ node }) {
  const { root, svg, legend, titleEl } = createChartFrame(node, 'Radar Chart');

  const update = (next) => {
    titleEl.textContent = resolveTitle(next, 'Radar Chart');
    const palette = resolvePalette(next);
    let axes = resolveAxes(next);
    let series = normalizeSeriesColors(resolveSeries(next, axes.length), palette);

    clearSvg(svg);
    const layout = getLayout({ pad: 20, padTop: 22, padBottom: 22, padLeft: 22, padRight: 22 });

    const radius = Math.min(layout.inner.w, layout.inner.h) / 2;
    const cx = layout.inner.x + layout.inner.w / 2;
    const cy = layout.inner.y + layout.inner.h / 2;
    const maxValue = resolveMax(series, next);

    drawRings(svg, cx, cy, radius, axes.length);
    drawAxes(svg, cx, cy, radius, axes);

    series.forEach((entry) => {
      const points = entry.values.map((value, idx) => {
        const angle = axisAngle(idx, axes.length);
        const r = radius * (toNumber(value, 0) / maxValue);
        return {
          x: cx + r * Math.cos(angle),
          y: cy + r * Math.sin(angle)
        };
      });
      const path = polygonPath(points);
      const shape = createSvgEl('path', {
        d: path,
        fill: entry.color,
        'fill-opacity': 0.18,
        stroke: entry.color,
        'stroke-width': 2
      });
      shape.classList.add('ew-chart-radar');
      svg.appendChild(shape);
    });

    renderLegend(legend, series, { show: next?.props?.legend !== false });
  };

  update(node);
  return { el: root, update };
}

function resolveAxes(node) {
  const axes = node?.props?.axes ?? node?.data?.axes;
  if (Array.isArray(axes) && axes.length) return axes;
  return DEFAULT_AXES;
}

function resolveSeries(node, length) {
  const raw = node?.data?.series ?? node?.props?.series;
  if (Array.isArray(raw)) {
    if (raw.length && typeof raw[0] === 'number') {
      return [{ name: node?.props?.label || 'Series', values: raw }];
    }
    if (raw.length && raw[0] && Array.isArray(raw[0])) {
      return raw.map((entry, i) => ({ name: `Series ${i + 1}`, values: entry }));
    }
    if (raw.length && raw[0] && typeof raw[0] === 'object') {
      return raw.map((entry, i) => ({
        name: entry.name || `Series ${i + 1}`,
        color: entry.color,
        values: normalizeValues(entry.values, length)
      }));
    }
  }

  return DEFAULT_SERIES.map((entry) => ({
    ...entry,
    values: normalizeValues(entry.values, length)
  }));
}

function normalizeValues(values, length) {
  const out = Array.isArray(values) ? values.slice(0, length) : [];
  while (out.length < length) out.push(0);
  return out;
}

function resolveMax(series, node) {
  const override = toNumber(node?.props?.max ?? node?.data?.max, NaN);
  if (Number.isFinite(override) && override > 0) return override;
  let max = 1;
  series.forEach((entry) => {
    entry.values.forEach((value) => {
      const n = toNumber(value, 0);
      if (n > max) max = n;
    });
  });
  return max || 1;
}

function drawRings(svg, cx, cy, radius, count) {
  const rings = 3;
  for (let i = 1; i <= rings; i += 1) {
    const r = (radius * i) / rings;
    const points = Array.from({ length: count }, (_, idx) => {
      const angle = axisAngle(idx, count);
      return {
        x: cx + r * Math.cos(angle),
        y: cy + r * Math.sin(angle)
      };
    });
    const ring = createSvgEl('path', {
      d: polygonPath(points),
      fill: 'none',
      stroke: 'var(--ew-ui-panel-border)',
      'stroke-opacity': 0.5
    });
    ring.classList.add('ew-chart-grid');
    svg.appendChild(ring);
  }
}

function drawAxes(svg, cx, cy, radius, axes) {
  axes.forEach((label, idx) => {
    const angle = axisAngle(idx, axes.length);
    const x = cx + radius * Math.cos(angle);
    const y = cy + radius * Math.sin(angle);
    const axis = createSvgEl('line', {
      x1: cx,
      y1: cy,
      x2: x,
      y2: y,
      stroke: 'var(--ew-ui-panel-border)',
      'stroke-opacity': 0.6
    });
    axis.classList.add('ew-chart-axis');
    svg.appendChild(axis);

    const labelOffset = 10;
    const lx = cx + (radius + labelOffset) * Math.cos(angle);
    const ly = cy + (radius + labelOffset) * Math.sin(angle);
    addLabel(svg, {
      x: lx,
      y: ly,
      text: label,
      anchor: lx < cx ? 'end' : 'start',
      className: 'ew-chart-label ew-chart-label-axis'
    });
  });
}

function axisAngle(index, total) {
  return -Math.PI / 2 + (Math.PI * 2 * index) / total;
}

function polygonPath(points) {
  if (!points.length) return '';
  const d = points.map((pt, idx) => `${idx === 0 ? 'M' : 'L'} ${pt.x} ${pt.y}`).join(' ');
  return `${d} Z`;
}
